/* eslint-disable max-depth */
'use client'

import { styled } from '@rybr/styled-modules'
import dynamic from 'next/dynamic'
import React, { ComponentProps, CSSProperties, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useScrollLock } from '..//hooks/useScrollLock'
import { KEYBOARD } from '../constants/constants.keyboards'
import { themeBaseConfig } from '../css/theme.base'
import { useFocusTrap } from '../hooks/useFocusTrap'

export type ModalProps = ComponentProps<typeof DynamicModal>

const MainContainer = styled.div<{
  zIndex?: number
  animationName?: string
  visibility: CSSProperties['visibility']
  pointerEvents: CSSProperties['pointerEvents']
}>('ModalMainContainer', ({ animationName, visibility, pointerEvents, zIndex }) => ({
  zIndex,
  visibility,
  pointerEvents,
  animationName
}))
const Backdrop = styled.div<{
  zIndex?: number
  backgroundColor: string
  animationName?: string
  visibility: CSSProperties['visibility']
  pointerEvents: CSSProperties['pointerEvents']
}>('ModalBackdrop', ({ backgroundColor, animationName, visibility, pointerEvents, zIndex }) => ({
  zIndex,
  visibility,
  pointerEvents,
  animationName,
  backgroundColor
}))

const DynamicModal: React.FC<
  PropsWithChildren<
    Omit<React.JSX.IntrinsicElements['div'], 'onClick' | 'onKeyDown'> & {
      isOpen: boolean
      onClose: () => void
      onKeyDown?: (...props: Parameters<React.KeyboardEventHandler>) => boolean
      openAnimation?: string
      closeAnimation?: string
      openBackdropAnimation?: string
      closeBackdropAnimation?: string
      backdropColor?: CSSProperties['backgroundColor']
      backdropClassName?: string
      zIndex?: number
      shouldScrollLock?: boolean
      afterOpen?: () => void
      afterClose?: () => void
    }
  >
> = ({
  isOpen,
  onClose,
  openAnimation = 'ModalFadeInAnim',
  closeAnimation = 'ModalFadeOutAnim',
  openBackdropAnimation = 'ModalFadeInBackdropAnim',
  closeBackdropAnimation = 'ModalFadeOutBackdropAnim',
  backdropColor = themeBaseConfig['color-neutral-6'],
  backdropClassName,
  zIndex,
  shouldScrollLock = true,
  afterOpen,
  afterClose,
  children,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { lockScroll, unlockScroll } = useScrollLock()
  const { enableFocusTrap, disableFocusTrap, onKeyDown, uniqueTag } = useFocusTrap(containerRef)
  const [visibility, setVisibility] = useState<CSSProperties['visibility']>('hidden')
  const [pointerEvents, setPointerEvents] = useState<CSSProperties['pointerEvents']>('none')

  useEffect(() => {
    // need to wait for mounting
    if (containerRef.current && isOpen) {
      setVisibility('visible')
      setPointerEvents('auto')
    }
  }, [isOpen, containerRef.current])

  useEffect(() => {
    // need to wait for mounting
    if (containerRef.current == null) {
      return
    }

    if (visibility === 'visible') {
      if (shouldScrollLock) {
        lockScroll()
      }

      enableFocusTrap()
    } else {
      unlockScroll()
      disableFocusTrap()
    }
  }, [visibility, containerRef.current, shouldScrollLock])

  useEffect(() => {
    return () => {
      unlockScroll()
      disableFocusTrap()
    }
  }, [])

  return createPortal(
    <>
      <MainContainer
        {...props}
        $zIndex={zIndex}
        $animationName={isOpen ? openAnimation : closeAnimation}
        $visibility={visibility}
        $pointerEvents={pointerEvents}
        aria-hidden={visibility === 'hidden'}
        inert={visibility === 'hidden'}
        ref={containerRef}
        onAnimationEnd={(e) => {
          if (e.animationName === closeAnimation) {
            disableFocusTrap()

            window.requestAnimationFrame(() => {
              setVisibility('hidden')
              setPointerEvents('none')
              afterClose?.()
            })
          } else if (e.animationName === openAnimation) {
            window.requestAnimationFrame(() => {
              afterOpen?.()
            })
          }
        }}
        onKeyDown={(e) => {
          // Early escape
          if (props.onKeyDown?.(e) === false) {
            return
          }

          // ADA close modal if ESC is pressed
          if (KEYBOARD.Escape.is(e)) {
            onClose()

            return
          }

          // ADA trap focus when tabbing
          onKeyDown(e)
        }}
      >
        {children}
      </MainContainer>
      <Backdrop
        onClick={onClose}
        aria-hidden={visibility === 'hidden'}
        inert={visibility === 'hidden'}
        $zIndex={zIndex}
        $backgroundColor={backdropColor}
        $animationName={isOpen ? openBackdropAnimation : closeBackdropAnimation}
        $visibility={visibility}
        $pointerEvents={pointerEvents}
        className={backdropClassName}
        // Tells focus trapper to not ignore this
        data-focus-trap-target-id={uniqueTag}
      />
    </>,
    document.body
  )
}

// Need to force to be dynamic/lazy since 'createPortal' is not currently supported for SSR
export const Modal = dynamic(() => Promise.resolve(DynamicModal), { ssr: false })
